import React, { Component } from "react";
import { SERVER_URL } from "../constants/variables";
import { getAuthHeaders, getEntityId, getUserRoleId } from "../utils/api";
import Sidebar from "./Sidebar";
import { LayoutDashboard, RefreshCw, Maximize2 } from "lucide-react";
import "./HomeComponent.css";

class HomeComponent extends Component {
  constructor(props) {
    super(props);

    this.toggle = this.toggle.bind(this);
    this.handleGlobalError = this.handleGlobalError.bind(this);
    this.state = {
      isOpen: false,
      user: {},
      tabs: [],
      iFrameUrl: "",
      dashboardNumber: this.props.dashboardNumber,
      idRoleMap: {},
      isLoading: true,
      currentTabName: "",
      iframeError: false,
    };
  }

  getRoles = () => {
    var requestOptions = {
      method: "GET",
      headers: getAuthHeaders(),
      redirect: "follow",
    };

    fetch(SERVER_URL + "/roles/getRoles", requestOptions)
      .then((response) => response.json())
      .then((result) => {
        if (result.success) {
          if (result.roles) {
            var map = {};
            result.roles.forEach((item) => {
              map[getEntityId(item)] = item.name;
            });
            this.setState({
              idRoleMap: map,
            });
          }
        }
      })
      .catch(() => {});
  };
  toggle() {
    this.setState({
      isOpen: !this.state.isOpen,
    });
  }

  getIframeUrl = () => {
    var stored = localStorage.getItem("dashboardNumber");
    var dashboardNumber = stored != null && stored !== "" ? stored : this.state.dashboardNumber ?? this.props.dashboardNumber;
    var num = parseInt(dashboardNumber, 10);
    if (isNaN(num) || num < 0) return;

    var requestOptions = {
      method: "GET",
      headers: getAuthHeaders(),
      redirect: "follow",
    };
    fetch(
      SERVER_URL + "/users/metabase/" + num,
      requestOptions
    )
      .then((response) => response.json())
      .then((result) => {
        if (result.success) {
          // Replace http:// with https:// to fix mixed content error in production
          const secureUrl = result.url ? result.url.replace(/^http:\/\//, 'https://') : result.url;
          const currentTab = this.state.tabs.find(tab => {
            const tabDashNum = typeof tab.dashboardNumber === 'number' 
              ? tab.dashboardNumber 
              : parseInt(tab.dashboardNumber, 10);
            return tabDashNum === num;
          });
          this.setState({ 
            iFrameUrl: secureUrl,
            isLoading: false,
            iframeError: false,
            currentTabName: currentTab ? currentTab.name : "",
            dashboardNumber: num
          });
          
          // Ensure localStorage is synced
          if (currentTab) {
            localStorage.setItem("dashboardNumber", num.toString());
          }
        } else {
          this.setState({ isLoading: false, iframeError: true });
        }
      })
      .catch((error) => {
        this.setState({ isLoading: false, iframeError: true });
      });
  };

  componentDidMount() {
    // Suppress iframe errors from Metabase dashboard (Morris.js, etc.)
    window.addEventListener('error', this.handleGlobalError, true);
    window.addEventListener('unhandledrejection', this.handleGlobalError, true);
    
    // Also suppress console errors from iframe
    const originalError = console.error;
    console.error = (...args) => {
      const message = args.join(' ');
      if (message.includes('Graph container element not found') || 
          message.includes('morris') ||
          message.includes('Morris')) {
        return; // Suppress Morris.js errors
      }
      originalError.apply(console, args);
    };
    this.originalConsoleError = originalError;
    
    var user = localStorage.getItem("user");
    if (user) {
      this.setState({ user: JSON.parse(user) });
      this.getRoles();
      this.getTabsByRole(getUserRoleId(JSON.parse(user)));
      this.getIframeUrl();
    } else {
      window.location = "/login";
    }
  }

  componentWillUnmount() {
    window.removeEventListener('error', this.handleGlobalError, true);
    window.removeEventListener('unhandledrejection', this.handleGlobalError, true);
    if (this.originalConsoleError) {
      console.error = this.originalConsoleError;
    }
  }

  handleGlobalError = (event) => {
    // Suppress Morris.js errors from Metabase dashboard iframe
    const errorMessage = event.message || event.error?.message || '';
    if (errorMessage.includes('Graph container element not found') || 
        errorMessage.includes('morris') ||
        (event.filename && event.filename.includes('morris'))) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    return true;
  }

  getTabsByRole = (roleId) => {
    var myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    const authHeaders = getAuthHeaders();
    if (authHeaders.Authorization) myHeaders.append("Authorization", authHeaders.Authorization);

    var raw = JSON.stringify({
      role: roleId,
    });

    var requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow",
    };

    fetch(SERVER_URL + "/tabs/getTabsByRole", requestOptions)
      .then((response) => response.json())
      .then((result) => {
        let tabs = result.tabs || [];
        
        // Filter tabs based on user's role ID matching accessRoles
        const userRoleId = roleId;
        if (userRoleId) {
          tabs = tabs.filter(tab => {
            if (!tab.accessRoles || !Array.isArray(tab.accessRoles) || tab.accessRoles.length === 0) {
              return false; // No access roles means no access
            }
            // Check if user's role ID matches any role ID in accessRoles
            return tab.accessRoles.some(role => {
              // Handle both 'id' and '_id' properties
              const roleIdToCheck = role.id || role._id;
              return roleIdToCheck === userRoleId || roleIdToCheck === parseInt(userRoleId, 10);
            });
          });
        }
        
        // Remove duplicate dashboardNumbers, keeping only the last occurrence
        const dashboardNumberMap = new Map();
        tabs.forEach((tab) => {
          const dashNum = typeof tab.dashboardNumber === 'number' 
            ? tab.dashboardNumber 
            : parseInt(tab.dashboardNumber, 10);
          if (!isNaN(dashNum)) {
            // Store the last occurrence of each dashboardNumber (overwrites previous)
            dashboardNumberMap.set(dashNum, tab);
          }
        });
        
        // Convert map values back to array (only last occurrence of each dashboardNumber)
        const uniqueTabs = Array.from(dashboardNumberMap.values());
        // Also keep tabs without valid dashboardNumber
        const tabsWithoutDashNum = tabs.filter(tab => {
          const dashNum = typeof tab.dashboardNumber === 'number' 
            ? tab.dashboardNumber 
            : parseInt(tab.dashboardNumber, 10);
          return isNaN(dashNum);
        });
        tabs = [...uniqueTabs, ...tabsWithoutDashNum];
        
        this.setState({
          tabs: tabs,
        });
        if (tabs.length > 0) {
          const storedDashboardNumber = localStorage.getItem("dashboardNumber");
          const dashboardNumber = storedDashboardNumber || tabs[0].dashboardNumber;
          if (!storedDashboardNumber) {
            localStorage.setItem("dashboardNumber", tabs[0].dashboardNumber);
          }
          const currentTab = tabs.find(tab => tab.dashboardNumber === parseInt(dashboardNumber, 10));
          if (currentTab) {
            this.setState({ currentTabName: currentTab.name });
          }
        }
        this.getIframeUrl();
      })
      .catch(() => {});
  };
  handleRefresh = () => {
    this.setState({ isLoading: true, iframeError: false });
    this.getIframeUrl();
  };

  handleIframeLoad = () => {
    this.setState({ isLoading: false });
  };

  handleIframeError = () => {
    this.setState({ isLoading: false, iframeError: true });
  };

  handleTabChange = (dashboardNumber) => {
    // Update dashboard number and reload iframe without page reload
    localStorage.setItem("dashboardNumber", dashboardNumber.toString());
    this.setState({ 
      dashboardNumber: dashboardNumber,
      isLoading: true,
      iframeError: false 
    });
    this.getIframeUrl();
  };

  render() {
    const { tabs, iFrameUrl, isLoading, iframeError, currentTabName } = this.state;
    
    return (
      <div className="app-layout">
        <Sidebar
          tabs={tabs}
          user={this.state.user}
          idRoleMap={this.state.idRoleMap}
          onTabChange={this.handleTabChange}
          currentDashboardNumber={this.state.dashboardNumber}
        />
        <div className="main-content-wrapper">
          {tabs && tabs.length > 0 ? (
            <div className="dashboard-container">
              <div className="dashboard-header">
                <div className="dashboard-header-left">
                  <div className="dashboard-icon">
                    <LayoutDashboard size={20} />
                  </div>
                  <div className="dashboard-title-section">
                    <h2 className="dashboard-title">{currentTabName || "Dashboard"}</h2>
                    <span className="dashboard-subtitle">Analytics & Insights</span>
                  </div>
                </div>
                <div className="dashboard-header-actions">
                  <button 
                    className="dashboard-action-btn"
                    onClick={this.handleRefresh}
                    title="Refresh Dashboard"
                  >
                    <RefreshCw size={16} />
                    <span>Refresh</span>
                  </button>
                  <button 
                    className="dashboard-action-btn"
                    title="Fullscreen"
                    onClick={() => {
                      const iframe = document.querySelector('.dashboard-iframe');
                      if (iframe && iframe.requestFullscreen) {
                        iframe.requestFullscreen();
                      }
                    }}
                  >
                    <Maximize2 size={16} />
                  </button>
                </div>
              </div>
              
              <div className="dashboard-content">
                {isLoading && (
                  <div className="dashboard-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading dashboard...</p>
                  </div>
                )}
                
                {iframeError && !isLoading && (
                  <div className="dashboard-error">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 8v4M12 16h.01" />
                    </svg>
                    <h3>Unable to Load Dashboard</h3>
                    <p>The dashboard could not be loaded. Please try again.</p>
                    <button className="dashboard-retry-btn" onClick={this.handleRefresh}>
                      Retry
                    </button>
                  </div>
                )}
                
                {iFrameUrl && !iframeError && (
                  <div className="dashboard-iframe-wrapper">
                    <iframe
                      src={iFrameUrl}
                      className="dashboard-iframe"
                      title={currentTabName || "Dashboard"}
                      onLoad={this.handleIframeLoad}
                      onError={this.handleIframeError}
                      allow="fullscreen"
                      allowFullScreen
                      sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
                    />
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="no-access-message">
              <div className="no-access-content">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
                <h3>No Access</h3>
                <p>You don't have access to any dashboards at the moment.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default HomeComponent;
